// A title as it should read — the browser-side twin of backend/domain/titles.py
// (plans/DOCUMENT_TITLES_PLAN.md §3). Documents cut out of a file before the
// normalizer existed still carry the shouted title in their file name; the row,
// the card and the export show it tidied until the file is next touched and the
// child is renamed in place. A title a person typed never comes through here.

const ACRONYMS = new Set(`AFIS AM ATF BAC BOLO BPD BWC CAD CD CDL CFS CI CJIN CJIS CODIS CPS CSI DA DEA DHS DL DMV DNA DOB DOC DOI DOJ DOT
DPHHS DRE DUI DV DVD EMS EMT ER FBI FY GCSO GFPD GPS GSR HPD ICU ID II III IV JP K9 LCSO LE LEO LLC MCSO MDT MHP
MIP MPD MRI MVD NCIC NIBRS OIS OPD PBT PC PD PDF PFMA PFO PM PO RN SANE SO SSN SWAT TV UCR US USA USB VIN YCSO`.split(/\s+/));
const ABBREVIATIONS = new Set('ST RD DR LN PL CT MR MRS MS JR SR LT SGT CPL PVT CAPT CMDR DET DPT DEPT BLVD HWY PKWY MT'.split(' '));
const SMALL_WORDS = new Set('a an the of and or nor for in on to at by from with as but per vs v re'.split(' '));

export function isShouted(text, minLetters = 4, share = 0.85) {
  const letters = String(text || '').match(/\p{L}/gu) || [];
  if (letters.length < minLetters) return false;
  const upper = letters.filter((c) => c === c.toUpperCase() && c !== c.toLowerCase()).length;
  return upper / letters.length >= share;
}

function caseWord(word, first, last) {
  const core = word.replace(/^[.,;:()[\]"'“”‘’]+|[.,;:()[\]"'“”‘’]+$/g, '');
  const bare = core.replace(/['’]/g, '');
  if (!bare) return word;
  const up = bare.toUpperCase();
  if (ACRONYMS.has(up)) return word === word.toUpperCase() ? word.toUpperCase() : word;
  if (/\d/.test(bare)) return word;
  if (bare.length >= 3 && bare.length <= 5 && bare === up && (up.endsWith('SO') || up.endsWith('PD')) && up !== 'ALSO') return word;
  if (!ABBREVIATIONS.has(up) && bare.length >= 3 && bare === up && !/[AEIOUY]/.test(up[0] === 'Y' ? up.slice(1) : up)) return word;
  const low = word.toLowerCase();
  if (!first && !last && SMALL_WORDS.has(bare.toLowerCase())) return low;
  return low.replace(/[a-z]+/g, (run, i) => ((i > 0 && "'’".includes(low[i - 1]) && run.length === 1) ? run : run[0].toUpperCase() + run.slice(1)));
}

export function titleCase(text) {
  const parts = String(text).split(/(\s+)/);
  const idx = parts.map((p, i) => (p && !/^\s+$/.test(p) ? i : -1)).filter((i) => i >= 0);
  if (!idx.length) return text;
  const [firstI, lastI] = [idx[0], idx[idx.length - 1]];
  return parts.map((p, i) => (idx.includes(i) ? caseWord(p, i === firstI, i === lastI) : p)).join('');
}

const PAGE_OF = /\bpage\s*\d{1,4}\s*(?:of|\/)\s*\d{1,4}\b/gi;
const PAGE_TRAIL = /[\s\-–—:,|]*\bpage\s*\d{1,4}\s*$/i;
const RE_PREFIX = /^\s*(?:re|subject|subj|regarding)\s*[:\-–—]\s*/i;
const EDGE_L = /^[\s\-–—:;,.|·•*#_]+/;
const EDGE_R = /[\s\-–—:;,|·•*#_]+$/;

/** The title a row or card shows for a raw title string; '' when nothing is left. */
export function displayTitle(raw) {
  let t = String(raw ?? '').replace(/[\x00-\x1f\x7f]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  t = t.replace(PAGE_OF, ' ').replace(PAGE_TRAIL, '').replace(RE_PREFIX, '').replace(/\s+/g, ' ').trim();
  t = t.replace(EDGE_L, '').replace(EDGE_R, '');
  if (!t) return '';
  return isShouted(t) ? titleCase(t) : t;
}
